import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query'
import * as z from 'zod'

import { customInstance } from '@/api/client'
import {
  type AffiliateAdminCouponSchema,
  affiliateAdminCouponSchema,
  type AffiliateCouponTagSchema,
  affiliateCouponTagSchema,
  type AffiliateCreateCouponSchema,
  type AffiliateCreateCouponTagRequest,
  type AffiliateUpdateCouponSchema,
} from '@/domain/coupons'

// TODO(MKT-745): After gto-wizard#2819 lands, regenerate the affiliates Orval
// client and replace these manual hooks.

export { getListPartnerCouponsQueryKey } from '@/api/gen'

type CreateCouponVariables = {
  partnerId: string
  data: AffiliateCreateCouponSchema
}

export const useCreateCoupon = (options?: {
  mutation?: UseMutationOptions<AffiliateAdminCouponSchema, unknown, CreateCouponVariables>
}) =>
  useMutation({
    mutationFn: async ({ partnerId, data }: CreateCouponVariables) => {
      const response = await customInstance<unknown>({
        url: `/v4/affiliates/admin/partners/${partnerId}/coupons/`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data,
      })

      return affiliateAdminCouponSchema.parse(response)
    },
    ...options?.mutation,
  })

type UpdateCouponVariables = {
  partnerId: string
  couponId: string
  data: AffiliateUpdateCouponSchema
}

export const useUpdateCoupon = (options?: {
  mutation?: UseMutationOptions<AffiliateAdminCouponSchema, unknown, UpdateCouponVariables>
}) =>
  useMutation({
    mutationFn: async ({ partnerId, couponId, data }: UpdateCouponVariables) => {
      const response = await customInstance<unknown>({
        url: `/v4/affiliates/admin/partners/${partnerId}/coupons/${couponId}/`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data,
      })

      return affiliateAdminCouponSchema.parse(response)
    },
    ...options?.mutation,
  })

export const getListCouponTagsQueryKey = (partnerId: string) =>
  [`/v4/affiliates/admin/partners/${partnerId}/coupon-tags/`] as const

const listCouponTags = async (partnerId: string): Promise<AffiliateCouponTagSchema[]> => {
  const data = await customInstance<unknown>({
    url: `/v4/affiliates/admin/partners/${partnerId}/coupon-tags/`,
    method: 'GET',
  })

  return z.array(affiliateCouponTagSchema).parse(data)
}

export const useListCouponTags = (
  partnerId: string,
  options?: Omit<UseQueryOptions<AffiliateCouponTagSchema[]>, 'queryFn' | 'queryKey'>,
) =>
  useQuery({
    queryKey: getListCouponTagsQueryKey(partnerId),
    queryFn: () => listCouponTags(partnerId),
    ...options,
  })

type CreateCouponTagVariables = {
  partnerId: string
  data: AffiliateCreateCouponTagRequest
}

export const useCreateCouponTag = (options?: {
  mutation?: UseMutationOptions<AffiliateCouponTagSchema, unknown, CreateCouponTagVariables>
}) =>
  useMutation({
    mutationFn: async ({ partnerId, data }: CreateCouponTagVariables) => {
      const response = await customInstance<unknown>({
        url: `/v4/affiliates/admin/partners/${partnerId}/coupon-tags/`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data,
      })

      return affiliateCouponTagSchema.parse(response)
    },
    ...options?.mutation,
  })

type DeleteCouponTagVariables = {
  partnerId: string
  tagId: string
}

export const useDeleteCouponTag = (options?: {
  mutation?: UseMutationOptions<void, unknown, DeleteCouponTagVariables>
}) =>
  useMutation({
    mutationFn: async ({ partnerId, tagId }: DeleteCouponTagVariables) => {
      await customInstance({
        url: `/v4/affiliates/admin/partners/${partnerId}/coupon-tags/${tagId}/`,
        method: 'DELETE',
      })
    },
    ...options?.mutation,
  })
